import { Reducer, Store, applyMiddleware, createStore } from 'redux';
import createSagaMiddleware, { Task } from 'redux-saga';
import { StopApplication } from './applicationActions';
import IBasicDriver from '../NativeDevice/IBasicDriver';
import { EventEmitter } from 'events';
import { actionEmitterMiddleware, createActionForwardMiddleware } from './applicationMiddlewares';

function initializeRedux<IState>(
	reducer: Reducer<IState>,
	getSaga: (store: Store<IState>) => Iterator<any>,
	onError: (e: Error) => void = (error: Error) => console.error(error),
	storeEmitter: EventEmitter,
) {
	const sagaMiddleware = createSagaMiddleware({ onError });
	const store = createStore<IState>(
		reducer,
		applyMiddleware(actionEmitterMiddleware, sagaMiddleware, createActionForwardMiddleware(storeEmitter)),
	);

	let task: Task | null = null;

	if (globalThis.__POSTPONE_SAGA_EXECUTION) {
		globalThis.__runSagas ??= [];
		globalThis.__runSagas.push(() => {
			task = sagaMiddleware.run(() => getSaga(store));
		});
	} else {
		task = sagaMiddleware.run(() => getSaga(store));
	}

	return {
		store,
		stopSaga: () => {
			if (task) {
				task.cancel();
				task = null;
			}
		},
	};
}

export async function createApplication<IState>(
	reducer: Reducer<IState>,
	getSaga: (store: Store<IState>) => Iterator<any>,
	getNativeDriver: () => IBasicDriver,
	onError?: (e: Error) => void,
) {
	const storeEmitter = new EventEmitter();
	const nativeDriver = getNativeDriver();
	const { store, stopSaga } = initializeRedux(reducer, getSaga, onError, storeEmitter);

	nativeDriver.start();

	return {
		store,
		storeEmitter,
		stop: () => {
			store.dispatch({ type: StopApplication });
			nativeDriver.stop();
			stopSaga();

			console.warn(
				"Application stopped and sagas cancelled. You should never see this message in production. The only way to trigger this should be a manual action by a developer. It's intended for debugging purposes.",
			);
		},
	};
}
